import React, { useContext, useEffect, useState } from 'react';
import { Form, Input, Row, Col, Select, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
import AuthContext from '@packages/contexts/auth/authContext';
import { Client } from '../../types/Client';
import { Role } from '../../types/Auth';
import { User } from '../../types/User';

interface ClientFormSectionInterfaceProps {
    formInstance: FormInstance;
    users: User[];
    client?: Client;
    isDetailMode?: boolean;
    enableControls?: boolean;
}

const clientFormFieldRules: { name: string; editableBy: string[] }[] = [
    { name: 'name', editableBy: [Role.StuartStaff, Role.SE] },
    { name: 'apiClientId', editableBy: [Role.StuartStaff, Role.SE] },
    { name: 'stuartClientId', editableBy: [Role.StuartStaff, Role.SE] },
    { name: 'storeId', editableBy: [Role.StuartStaff, Role.SE] },
    { name: 'linkedUsers', editableBy: [Role.StuartStaff, Role.SE] },
    { name: 'jetToken', editableBy: [Role.StuartStaff, Role.SE] },
];

const displayClientFormFieldRules: { name: string; displayFor: string[] }[] = [
    { name: 'name', displayFor: [Role.Client, Role.StuartStaff, Role.SE] },
    { name: 'apiClientId', displayFor: [Role.Client, Role.StuartStaff, Role.SE] },
    { name: 'stuartClientId', displayFor: [Role.Client, Role.StuartStaff, Role.SE] },
    { name: 'storeId', displayFor: [Role.Client, Role.StuartStaff, Role.SE] },
    { name: 'linkedUsers', displayFor: [Role.StuartStaff, Role.SE] },
    { name: 'jetToken', displayFor: [Role.Client, Role.StuartStaff, Role.SE] },
];

const getLinkedUsers = (users: User[], client?: Client): User[] => {
    const linkedUserIds = client?.users?.map(({ id }) => id) || [];
    return users.filter(({ id }) => linkedUserIds.includes(id));
};

const ClientFormSection: React.FC<ClientFormSectionInterfaceProps> = (
    props: ClientFormSectionInterfaceProps,
): React.ReactElement => {
    const { formInstance, client, isDetailMode, enableControls, users } = props;
    const { user } = useContext(AuthContext);
    const [externalId, setExternalId] = useState<string[]>([]);

    const isReadOnlyField = (fieldName: string): boolean => {
        const userRole = user!.role;
        return (
            isDetailMode ||
            !(userRole && clientFormFieldRules.find((field) => field.name === fieldName)?.editableBy.includes(userRole))
        );
    };

    const shouldDisplayField = (fieldName: string): boolean => {
        const userRole = user!.role;
        return (
            !!userRole &&
            displayClientFormFieldRules.find((field) => field.name === fieldName)!.displayFor.includes(userRole)
        );
    };

    useEffect(() => {
        if (client) {
            const [retailerId, storeId, retailerCode] = (client?.storeId || '').split(';');
            formInstance.setFields([
                { name: 'name', value: client.name },
                { name: 'apiClientId', value: client.apiClientId },
                { name: 'stuartClientId', value: client.stuartClientId },
                { name: 'linkedUserIds', value: getLinkedUsers(users, client).map(({ id }) => id) },
                { name: 'clientExternalId', value: { storeId, retailerId, retailerCode } },
                { name: 'jetToken', value: client.jetToken },
            ]);
            setExternalId([retailerId, storeId, retailerCode]);
        }
    }, [client, users]);

    const renderSelectedOptions = (): JSX.Element[] | string => {
        const options = getLinkedUsers(users, client).map(({ email }: { email: string }) => {
            return <Tag key={email}>{email}</Tag>;
        });
        return options && options.length > 0 ? options : '-';
    };

    // eslint-disable-next-line react/no-unused-prop-types
    const linkedUsersOptions = users?.map(({ id, email }: { id: number; email: string }) => (
        <Select.Option key={email} value={id}>
            {email}
        </Select.Option>
    ));

    return (
        <>
            <div id="client-section">
                <Row gutter={24}>
                    <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item
                            label="Name"
                            name="name"
                            id="client__name"
                            rules={[{ required: !isReadOnlyField('name'), message: 'Field name is required' }]}
                        >
                            {isReadOnlyField('name') ? (
                                <span className="ant-form-text text-secondary text-break">{client?.name}</span>
                            ) : (
                                <Input
                                    name="name"
                                    id="client__name"
                                    title="Name"
                                    autoComplete="off"
                                    autoFocus
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item
                            label="Stuart Client ID"
                            name="stuartClientId"
                            id="client__stuart-client-id"
                            rules={[
                                {
                                    required: !isReadOnlyField('stuartClientId'),
                                    message: 'Field Stuart Client ID is required',
                                },
                            ]}
                        >
                            {isReadOnlyField('stuartClientId') ? (
                                <span className="ant-form-text text-secondary text-break">
                                    {client?.stuartClientId}
                                </span>
                            ) : (
                                <Input
                                    name="stuartClientId"
                                    id="client__stuartClientId"
                                    title="Stuart Client ID"
                                    autoComplete="off"
                                    autoFocus
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                        <Form.Item
                            label="API Client Id"
                            name="apiClientId"
                            id="client__api-client-id"
                            rules={[
                                {
                                    required: !isReadOnlyField('apiClientId'),
                                    message: 'Field api client id is required',
                                },
                            ]}
                        >
                            {isReadOnlyField('apiClientId') ? (
                                <span className="ant-form-text text-secondary text-break">{client?.apiClientId}</span>
                            ) : (
                                <Input
                                    name="apiClientId"
                                    id="client__api-client-id"
                                    title="API Client Id"
                                    autoComplete="off"
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    {shouldDisplayField('linkedUsers') && (
                        <Col
                            span={8}
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 8 }}
                            title="Authorized dashboard users"
                        >
                            <Form.Item
                                name="linkedUserIds"
                                id="client__linked-user-ids"
                                /* eslint-disable prettier/prettier */
                                /* eslint-disable react/jsx-wrap-multilines */
                                label={
                                    <>
                                        <span className="mr-1">Authorized users</span>
                                        <Tooltip title="Only these have access to this client">
                                            <InfoCircleOutlined className="text-muted" />
                                        </Tooltip>
                                    </>
                                }
                                /* eslint-enable react/jsx-wrap-multilines */
                                /* eslint-enable prettier/prettier */
                            >
                                {isReadOnlyField('linkedUsers') ? (
                                    <span>{renderSelectedOptions()}</span>
                                ) : (
                                    <Select
                                        mode="multiple"
                                        className="w-100"
                                        placeholder="Select one or more users"
                                        disabled={!enableControls}
                                        allowClear
                                        showArrow
                                    >
                                        {linkedUsersOptions}
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>
                    )}
                    <Col span={5} xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 5 }}>
                        <Form.Item
                            /* eslint-disable react/jsx-wrap-multilines */
                            label={
                                <>
                                    <span className="mr-1">Retailer ID</span>
                                    <Tooltip title="This field is used by Woop client only">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            name={['clientExternalId', 'retailerId']}
                            id="client__retailer-id"
                        >
                            {isReadOnlyField('storeId') ? (
                                <span className="ant-form-text text-secondary text-break">{externalId[0]}</span>
                            ) : (
                                <Input
                                    name="clientExternalId.retailerId"
                                    id="client__retailer-id_input"
                                    title="Woop retailer Id: used to identify the client from Woop API's payload"
                                    autoComplete="off"
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col offset={1} span={5} xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 5 }}>
                        <Form.Item
                            /* eslint-disable react/jsx-wrap-multilines */
                            label={
                                <>
                                    <span className="mr-1">Store ID</span>
                                    <Tooltip title="This field is used by Woop and Monoprix clients only">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            name={['clientExternalId', 'storeId']}
                            id="client__store-id"
                        >
                            {isReadOnlyField('storeId') ? (
                                <span className="ant-form-text text-secondary text-break">{externalId[1]}</span>
                            ) : (
                                <Input
                                    name="clientExternalId.clientStoreId"
                                    id="client__store-id_input"
                                    title="Woop store Id: used to identify the client from Woop API's payload"
                                    autoComplete="off"
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={5} xs={{ span: 12 }} sm={{ span: 12 }} md={{ span: 5 }}>
                        <Form.Item
                            /* eslint-disable react/jsx-wrap-multilines */
                            label={
                                <>
                                    <span className="mr-1">Retailer code</span>
                                    <Tooltip title="This field is used by Woop client only">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            name={['clientExternalId', 'retailerCode']}
                            id="client__retailer-code"
                        >
                            {isReadOnlyField('storeId') ? (
                                <span className="ant-form-text text-secondary text-break">{externalId[2]}</span>
                            ) : (
                                <Input
                                    name="clientExternalId.retailerCode"
                                    id="client__retailer-code_input"
                                    title="Woop retailer code: used to identify the client from Woop API's payload"
                                    autoComplete="off"
                                    disabled={!enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    {shouldDisplayField('jetToken') && (
                        <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                            <Form.Item
                                label={
                                    <>
                                        <span className="mr-1">JustEat Token</span>
                                        <Tooltip title="Used to identify a JustEat client when this token is sent in the 'JE-API-KEY' header of a request">
                                            <InfoCircleOutlined className="text-muted" />
                                        </Tooltip>
                                    </>
                                }
                                name="jetToken"
                                id="client__jetToken"
                            >
                                {isReadOnlyField('jetToken') ? (
                                    <span className="ant-form-text text-secondary text-break">{client?.jetToken}</span>
                                ) : (
                                    <Input
                                        name="jetToken"
                                        id="client__jetToken"
                                        title="Token sent in the 'JE-API-KEY' header"
                                        autoComplete="off"
                                        disabled={!enableControls}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </div>
        </>
    );
};

export default ClientFormSection;
